import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { buildGenreNames, getReleaseYear } from "../utils/movie";

const FavoritesContext = createContext(null);
const STORAGE_KEY = "@novafliz:favorites";
const LEGACY_STORAGE_KEY = "@filmes";

function normalizeStoredMovie(movie) {
  if (!movie?.id || !movie?.title) {
    return null;
  }

  const releaseDate = movie.releaseDate || movie.release_date || "";

  return {
    id: movie.id,
    title: movie.title,
    originalTitle: movie.originalTitle || movie.original_title || movie.title,
    overview: movie.overview || "",
    posterPath: movie.posterPath || movie.poster_path || "",
    backdropPath: movie.backdropPath || movie.backdrop_path || "",
    rating: movie.rating ?? movie.vote_average ?? 0,
    voteCount: movie.voteCount ?? movie.vote_count ?? 0,
    popularity: movie.popularity ?? 0,
    releaseDate,
    releaseYear: movie.releaseYear || getReleaseYear(releaseDate),
    adult: movie.adult || false,
    genreNames: movie.genreNames?.length ? movie.genreNames : buildGenreNames(movie),
    genres: movie.genres || [],
    mediaType: movie.mediaType || movie.media_type || "movie",
  };
}

function getInitialFavorites() {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
    const parsedFavorites = storedValue ? JSON.parse(storedValue) : [];
    return parsedFavorites.map(normalizeStoredMovie).filter(Boolean);
  } catch (error) {
    return [];
  }
}

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState(getInitialFavorites);

  const persistFavorites = useCallback((nextFavorites) => {
    const normalizedFavorites = nextFavorites.map(normalizeStoredMovie).filter(Boolean);
    setFavorites(normalizedFavorites);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedFavorites));
  }, []);

  const isFavorite = useCallback(
    (movieId) => favorites.some((movie) => movie.id === movieId),
    [favorites]
  );

  const addFavorite = useCallback(
    (movie) => {
      const normalizedMovie = normalizeStoredMovie(movie);

      if (!normalizedMovie) {
        return;
      }

      if (favorites.some((item) => item.id === normalizedMovie.id)) {
        toast.info("Esse titulo ja esta na sua lista.");
        return;
      }

      const nextFavorites = [normalizedMovie, ...favorites];
      persistFavorites(nextFavorites);
      toast.success("Titulo salvo na sua lista.");
    },
    [favorites, persistFavorites]
  );

  const removeFavorite = useCallback(
    (movieId) => {
      const nextFavorites = favorites.filter((movie) => movie.id !== movieId);
      persistFavorites(nextFavorites);
      toast.success("Titulo removido da sua lista.");
    },
    [favorites, persistFavorites]
  );

  const toggleFavorite = useCallback(
    (movie) => {
      const normalizedMovie = normalizeStoredMovie(movie);

      if (!normalizedMovie) {
        return;
      }

      if (favorites.some((item) => item.id === normalizedMovie.id)) {
        removeFavorite(normalizedMovie.id);
        return;
      }

      addFavorite(normalizedMovie);
    },
    [addFavorite, favorites, removeFavorite]
  );

  const value = useMemo(
    () => ({
      favorites,
      isFavorite,
      addFavorite,
      removeFavorite,
      toggleFavorite,
    }),
    [addFavorite, favorites, isFavorite, removeFavorite, toggleFavorite]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavoritesContext() {
  const context = useContext(FavoritesContext);

  if (!context) {
    throw new Error("useFavoritesContext precisa ser usado dentro de FavoritesProvider.");
  }

  return context;
}
